import React, { useState } from 'react';
import {
  useApolloClient,
  type ApolloClient,
  type NormalizedCacheObject,
} from '@apollo/client';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import {
  Alert,
  type AlertDetails,
  ButtonContainer,
  Dialog,
  type DialogDetails,
  Loading,
  SearchField,
} from '../../../../components';
import { handlePageLoading } from '../../../../utils';
import { useGetApplicantByFilter } from '../../../Management/SuperAdmin/graphql/useGetApplicantByFilter';
import {
  useUpdateApplicantInformation,
  type ApplicantInformation,
} from '../../../UserInfo/graphql';
import {
  identityTypeFullOptions,
  IS_LOCAL,
  localTypeOptions,
  NEED_STUDENT_VISA,
  needStudentVisaOptions,
  PAGE_TEXT,
} from '../../../Signup/components';
import { Box, Button, Stack, Typography } from '@mui/material';
import {
  type InputTextProps,
  returnedComponent,
} from '../../../Application/components/returnFieldComponent';
import { useAdminDeleteVisa, useAdminRegenerateVisa } from '../../../Signup';
import { type StepperFormContentValues } from '../../../Signup/components/types';

const defaultApplicant = {
  email: '',
  givenName: '',
  surname: '',
  gender: '',
  dateOfBirth: '',
  identityType: '',
  identityDocumentNumber: '',
  identityDocumentType: '',
  isLocal: false,
  needStudentVisa: false,
  nationality: '',
  province: '',
  addressCountryRegion: '',
  addressLine1: '',
  mobileNumber: '',
  telephoneNumber: '',
  id: '',
  disabilitySpecialType: '',
  disabilitySpecialDetail: '',
};

type VisaTypeUpdateProps = {
  refetchList: () => void;
};

export const VisaTypeUpdate: React.FC<VisaTypeUpdateProps> = props => {
  const { refetchList } = props;

  const [searchContent, setSearchContent] = useState('');

  const [openAlert, setOpenAlert] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDetails, setDialogDetails] = useState<DialogDetails>({
    title: '',
  });

  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const regenerateVisa = useAdminRegenerateVisa();
  const deleteVisa = useAdminDeleteVisa();
  const updateApplicantInformation = useUpdateApplicantInformation();

  const handleContentChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setSearchContent(event.target.value);
    formik.resetForm();
  };

  const handleSearch = async (): Promise<void> => {
    formik.resetForm();
    await handlePageLoading(setPageLoading, async () => {
      await useGetApplicantByFilter(client, {
        email: { eq: searchContent },
      })
        .then(async applicant => {
          if (applicant.id as boolean) {
            await formik.setValues({
              ...applicant,
              isLocal: (applicant.isLocal as string)
                ? IS_LOCAL.LOCAL
                : IS_LOCAL.NON_LOCAL,
              needStudentVisa: (applicant.needStudentVisa as string)
                ? NEED_STUDENT_VISA.YES
                : NEED_STUDENT_VISA.NO,
            });
          } else {
            setAlertDetails({
              alertContent: `No Record.`,
              alertType: 'warning',
            });
            setOpenAlert(true);
          }
        })
        .catch(error => {
          setAlertDetails({
            alertContent: `Error Occurs. ${(error as Error).message}`,
            alertType: 'error',
          });
          setOpenAlert(true);
        });
    });
  };

  const handleClear = (): void => {
    formik.resetForm();
    setSearchContent('');
  };

  // function for update applicant info,
  // using the same api as update applicant, but in this page just update "Visa Type", "Local Indicator", "Need Student Visa"
  const handlePreprocessUpdateApplicant = (
    data: any,
  ): { id: string; input: any } => {
    const {
      id,
      surname,
      givenName,
      chineseName,
      identityDocumentNumber,
      identityDocumentType,
      identityType,
      isLocal,
      needStudentVisa,
      telephoneNumber,
      disabilitySpecialType,
      disabilitySpecialDetail,
      ...others
    } = data;

    const input: ApplicantInformation = {
      name: { surname, givenName, chineseName },
      identity: {
        identityType,
        identityDocumentNumber,
        identityDocumentType,
        isLocal: isLocal === IS_LOCAL.LOCAL,
        needStudentVisa: needStudentVisa === NEED_STUDENT_VISA.YES,
      },
      ...(disabilitySpecialType !== 'NA' && disabilitySpecialType !== undefined
        ? {
            disabilitySpecial: {
              disabilitySpecialType,
              disabilitySpecialDetail,
            },
          }
        : { disabilitySpecial: null }),
      telephoneNumber: !telephoneNumber ? '' : telephoneNumber, // to override the previously value
      ...others,
    };
    return { id, input };
  };

  const handleUpdateApplicant = async (): Promise<void> => {
    await handlePageLoading(setPageLoading, async () => {
      const { id, input } = handlePreprocessUpdateApplicant(formik.values);

      await updateApplicantInformation({
        variables: {
          id,
          input,
        },
      })
        .then(res => {
          return res?.data?.updateApplicant;
        })
        .then(applicant => {
          if (applicant._id === id) {
            setAlertDetails({
              alertContent: 'User Information Updated Successfully.',
              alertType: 'success',
            });
          } else {
            setAlertDetails({
              alertContent: 'Cannot Update User Information.',
              alertType: 'error',
            });
          }
        })
        .catch(error => {
          setAlertDetails({
            alertContent: `Error Updating User Information. ${
              (error as Error).message
            }`,
            alertType: 'error',
          });
        })
        .finally(() => {
          setOpenAlert(true);
        });
    });
  };

  const formik = useFormik<ApplicantInformation>({
    initialValues: defaultApplicant,
    validationSchema: object({
      identityType: string().required(PAGE_TEXT.REQUIRED),
      identityDocumentNumber: string().required(PAGE_TEXT.REQUIRED),
      identityDocumentType: string().required(PAGE_TEXT.REQUIRED),
    }),
    onSubmit: handleUpdateApplicant,
  });

  // Visa operation stuff
  const visaActionMapping = {
    add: {
      dialogDetails: {
        title: PAGE_TEXT.VISA_COMPENSATION.ADD.DIALOG.TITLE,
        content: PAGE_TEXT.VISA_COMPENSATION.ADD.DIALOG.CONTENT,
        confirmAction: PAGE_TEXT.VISA_COMPENSATION.ADD.DIALOG.CONFIRM_ACTION,
        cancelAction: PAGE_TEXT.VISA_COMPENSATION.ADD.DIALOG.CANCEL_ACTION,
        handleDialogConfirmAction: async () => {
          await handleVisaCompensation('add');
        },
      },
      execute: regenerateVisa,
      reminderContent: PAGE_TEXT.VISA_COMPENSATION.ADD.REMINDER_CONTENT,
    },
    delete: {
      dialogDetails: {
        title: PAGE_TEXT.VISA_COMPENSATION.DELETE.DIALOG.TITLE,
        content: PAGE_TEXT.VISA_COMPENSATION.DELETE.DIALOG.CONTENT,
        confirmAction: PAGE_TEXT.VISA_COMPENSATION.DELETE.DIALOG.CONFIRM_ACTION,
        cancelAction: PAGE_TEXT.VISA_COMPENSATION.DELETE.DIALOG.CANCEL_ACTION,
        handleDialogConfirmAction: async () => {
          await handleVisaCompensation('delete');
        },
      },
      execute: deleteVisa,
      reminderContent: PAGE_TEXT.VISA_COMPENSATION.DELETE.REMINDER_CONTENT,
    },
    reminder: {
      dialogDetails: {
        title: PAGE_TEXT.VISA_COMPENSATION.REMINDER.DIALOG.TITLE,
        content: PAGE_TEXT.VISA_COMPENSATION.REMINDER.DIALOG.CONTENT,
        confirmAction:
          PAGE_TEXT.VISA_COMPENSATION.REMINDER.DIALOG.CONFIRM_ACTION,
        handleDialogConfirmAction: () => {
          setDialogOpen(false);
        },
      },
    },
  };

  const handleVisaCompensation = async (
    action: 'add' | 'delete',
  ): Promise<void> => {
    setDialogOpen(false);

    await handlePageLoading(setPageLoading, async () => {
      const visaAction = visaActionMapping[action];
      const reminder = visaActionMapping.reminder;

      await visaAction
        .execute({
          variables: { applicantId: formik.values.id },
        })
        .then(() => {
          reminder.dialogDetails.content = visaAction.reminderContent;
          setDialogDetails(reminder.dialogDetails);
          setDialogOpen(true);
        })
        .catch(error => {
          setAlertDetails({
            alertContent: 'Error Occurs. ' + (error as Error)?.message,
            alertType: 'error',
          });
          setOpenAlert(true);
        })
        .finally(() => {
          refetchList();
        });
    });
  };

  const handleVisaCompensationDialog = (action: 'add' | 'delete'): void => {
    setDialogDetails(visaActionMapping[action].dialogDetails);
    setDialogOpen(true);
  };

  // a 2d table, if the cell is set to be null, other column will automatically occupy the space
  const renderTable: Array<
    Array<
      | (InputTextProps<StepperFormContentValues> & {
          isPresent: boolean;
          fieldComponent:
            | (() => React.ReactNode)
            | ((returnedComponent: React.ReactNode) => React.ReactNode);
        })
      | null
    >
  > = [
    [
      {
        label: PAGE_TEXT.VISA_TYPE,
        isPresent: true,
        type: 'select',
        options: identityTypeFullOptions,
        disabled: false,
        required: true,
        name: 'identityType',
        fieldComponent: returnedComponent => {
          return (
            <>
              <Stack direction={'row'} alignItems={'center'}>
                <Box className="min-w-max">{PAGE_TEXT.VISA_TYPE}:</Box>
                <Box width={'100%'}>{returnedComponent}</Box>
              </Stack>
            </>
          );
        },
      },
      {
        label: PAGE_TEXT.IS_LOCAL,
        isPresent: true,
        type: 'select',
        options: localTypeOptions,
        disabled: false,
        required: true,
        name: 'isLocal',
        fieldComponent: returnedComponent => {
          return (
            <>
              <Stack direction={'row'} alignItems={'center'}>
                <Box className="min-w-max">{PAGE_TEXT.IS_LOCAL}:</Box>
                <Box width={'100%'}>{returnedComponent}</Box>
              </Stack>
            </>
          );
        },
      },
      {
        label: PAGE_TEXT.NEED_STUDENT_VISA,
        isPresent: true,
        type: 'select',
        options: needStudentVisaOptions,
        disabled: false,
        required: true,
        name: 'needStudentVisa',
        fieldComponent: returnedComponent => {
          return (
            <>
              <Stack direction={'row'} alignItems={'center'}>
                <Box className="min-w-max">{PAGE_TEXT.NEED_STUDENT_VISA}:</Box>
                <Box width={'100%'}>{returnedComponent}</Box>
              </Stack>
            </>
          );
        },
      },
    ],
    [
      {
        label: 'For changes to applicants who have accepted an offer',
        isPresent: true,
        type: 'select',
        options: localTypeOptions,
        disabled: false,
        required: false,
        name: 'identityTypeCompany',
        fieldComponent: () => {
          return (
            <>
              <Stack direction={'column'}>
                <Box className="min-w-max">
                  {PAGE_TEXT.VISA_TYPE_UPDATE.CHANGE_VISA_COMPENSATION}
                </Box>
                <ButtonContainer spacing={2}>
                  <Button
                    className="!w-40 !h-16 !normal-case"
                    variant="contained"
                    onClick={() => {
                      handleVisaCompensationDialog('add');
                    }}
                  >
                    {PAGE_TEXT.VISA_COMPENSATION.ADD.LABEL}
                  </Button>
                  <Button
                    className="!w-40 !h-16 !normal-case"
                    variant="contained"
                    onClick={() => {
                      handleVisaCompensationDialog('delete');
                    }}
                  >
                    {PAGE_TEXT.VISA_COMPENSATION.DELETE.LABEL}
                  </Button>
                </ButtonContainer>
              </Stack>
            </>
          );
        },
      },
      null,
      null,
    ],
  ];

  return (
    <Box className="border border-gray-300 rounded-lg p-4 mb-4">
      <Stack direction={'row'} alignItems={'center'}>
        <Typography className="min-w-max" fontWeight={'bold'}>
          {PAGE_TEXT.VISA_TYPE_UPDATE.TITLE}
        </Typography>
        <SearchField
          disabled={searchContent.length < 1}
          content={searchContent}
          placeholder={"Applicant's Email"}
          handleContentChange={handleContentChange}
          handleSearch={handleSearch}
        />
      </Stack>
      {formik.values.id && (
        <>
          <Box className="!my-4 w-full">
            {renderTable.map((row: any, index1: number) => {
              return (
                <Stack
                  className="!my-8"
                  direction={'row'}
                  spacing={2}
                  key={index1}
                >
                  {row.map((field: any, index2: number) => {
                    if (field as boolean) {
                      const { label, fieldComponent, ...others } = field;

                      const item: any = {
                        ...others,
                      };
                      return (
                        <Box className="w-full" key={index2}>
                          {fieldComponent(returnedComponent({ item, formik }))}
                        </Box>
                      );
                    } else {
                      return <Box className="w-0" key={index2}></Box>;
                    }
                  })}
                </Stack>
              );
            })}
          </Box>
          <ButtonContainer spacing={4} justifyContent="center">
            <Button
              className="!normal-case"
              variant="contained"
              onClick={formik.submitForm}
            >
              {PAGE_TEXT.VISA_TYPE_UPDATE.CONFIRM}
            </Button>
            <Button
              className="!normal-case"
              variant="outlined"
              onClick={handleClear}
            >
              {PAGE_TEXT.VISA_TYPE_UPDATE.CLEAR}
            </Button>
          </ButtonContainer>
        </>
      )}

      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <Dialog
        openDialog={dialogOpen}
        dialogTitle={dialogDetails.title}
        dialogAction={dialogDetails.confirmAction}
        cancelAction={dialogDetails.cancelAction ?? undefined}
        onDialogAction={dialogDetails.handleDialogConfirmAction}
        onDialogClose={() => {
          setDialogOpen(false);
        }}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {dialogDetails.content}
        </Typography>
      </Dialog>
      <Loading openLoading={pageLoading} />
    </Box>
  );
};
